/**
 * Per-player fielding metrics derived from this game's `game_fielding` entries.
 *
 * Surfaces are kept narrow and pure so the UI can reactively recompute as the
 * coach edits inning-by-inning fielding without hitting the network.
 */

export interface FieldingDepthChartEntry {
  position: string;
  depth: number; // inning number
  player_id: string | null;
}

export interface GameFieldingPlayerStats {
  /** Innings pitched through (and including) the current inning. */
  inningsPitched: number;
  /** Innings caught through (and including) the current inning. */
  inningsCaught: number;
  /**
   * Innings 1..currentInning-1 where the player has no fielding assignment.
   * Current inning is excluded so coaches see "already sat" vs "about to sit".
   */
  inningsSat: number;
  /**
   * Total innings this player has been assigned a field position so far
   * (through the current inning, includes pitcher/catcher counts).
   */
  inningsPlayed: number;
  /**
   * Pitches thrown so far, sourced from `sport_stats` when available.
   * `null` means no value recorded; UI should hide the value.
   */
  pitchesThrown: number | null;
  /**
   * Estimated pitches caught so far, derived from fielding assignments + pitcher
   * pitch counts.  For each inning this player caught, we add that inning's
   * pitcher's pitches/inning rate.  `null` means the player never caught or no
   * pitch data was available.
   */
  pitchesCaught: number | null;
}

export function emptyFieldingPlayerStats(): GameFieldingPlayerStats {
  return {
    inningsPitched: 0,
    inningsCaught: 0,
    inningsSat: 0,
    inningsPlayed: 0,
    pitchesThrown: null,
    pitchesCaught: null,
  };
}

interface ComputeArgs {
  depthChart: FieldingDepthChartEntry[];
  playerIds: string[];
  currentInning: number;
  /** Optional map of player_id -> pitches thrown in this game (raw stat). */
  pitchesByPlayerId?: Map<string, number>;
}

/**
 * Compute per-player fielding stats for the current inning context.
 *
 * - `inningsPitched` / `inningsCaught` are cumulative through the current
 *   inning (inclusive) — they reflect total load so far for this game.
 * - `inningsSat` is exclusive of the current inning so the coach can see who
 *   has already had a rest while making the next assignment.
 */
export function computeGameFieldingPlayerStats({
  depthChart,
  playerIds,
  currentInning,
  pitchesByPlayerId,
}: ComputeArgs): Map<string, GameFieldingPlayerStats> {
  const result = new Map<string, GameFieldingPlayerStats>();
  for (const id of playerIds) {
    result.set(id, emptyFieldingPlayerStats());
  }

  const assignmentsByInning = new Map<number, Set<string>>();
  for (const entry of depthChart) {
    if (!entry.player_id) continue;
    if (!Number.isFinite(entry.depth) || entry.depth < 1) continue;

    if (entry.depth <= currentInning) {
      const stats =
        result.get(entry.player_id) ?? emptyFieldingPlayerStats();
      stats.inningsPlayed += 1;
      if (entry.position === 'P') stats.inningsPitched += 1;
      if (entry.position === 'C') stats.inningsCaught += 1;
      result.set(entry.player_id, stats);
    }

    if (entry.depth < currentInning) {
      let set = assignmentsByInning.get(entry.depth);
      if (!set) {
        set = new Set();
        assignmentsByInning.set(entry.depth, set);
      }
      set.add(entry.player_id);
    }
  }

  for (let inning = 1; inning < currentInning; inning++) {
    const assigned = assignmentsByInning.get(inning) ?? new Set<string>();
    for (const id of playerIds) {
      if (!assigned.has(id)) {
        const stats = result.get(id) ?? emptyFieldingPlayerStats();
        stats.inningsSat += 1;
        result.set(id, stats);
      }
    }
  }

  if (pitchesByPlayerId) {
    // Apply pitchesThrown for pitchers.
    for (const [pid, count] of pitchesByPlayerId.entries()) {
      const stats = result.get(pid);
      if (stats && Number.isFinite(count)) {
        stats.pitchesThrown = count;
      }
    }

    // Compute pitchesCaught for catchers.
    // Strategy: for each inning, find the pitcher and catcher.  Distribute
    // the pitcher's total pitch count evenly across their innings to get a
    // per-inning pitch rate, then credit that rate to the catcher who was
    // behind the plate that inning.

    // Build maps: inning -> pitcher_id, inning -> catcher_id
    const inningToPitcher = new Map<number, string>();
    const inningToCatcher = new Map<number, string>();
    for (const entry of depthChart) {
      if (!entry.player_id || !Number.isFinite(entry.depth)) continue;
      if (entry.depth < 1 || entry.depth > currentInning) continue;
      if (entry.position === 'P') inningToPitcher.set(entry.depth, entry.player_id);
      if (entry.position === 'C') inningToCatcher.set(entry.depth, entry.player_id);
    }

    // Count total innings pitched per pitcher (for rate calculation).
    const inningsPitchedCount = new Map<string, number>();
    for (const pitcherId of inningToPitcher.values()) {
      inningsPitchedCount.set(pitcherId, (inningsPitchedCount.get(pitcherId) ?? 0) + 1);
    }

    // Accumulate pitches-caught totals (as floats, rounded at the end).
    const catcherPitchFloats = new Map<string, number>();
    for (let inning = 1; inning <= currentInning; inning++) {
      const pitcherId = inningToPitcher.get(inning);
      const catcherId = inningToCatcher.get(inning);
      if (!pitcherId || !catcherId) continue;

      const totalPitches = pitchesByPlayerId.get(pitcherId);
      const totalInningsPitched = inningsPitchedCount.get(pitcherId) ?? 0;
      if (!totalPitches || !Number.isFinite(totalPitches) || totalInningsPitched === 0) continue;

      const pitchesThisInning = totalPitches / totalInningsPitched;
      catcherPitchFloats.set(catcherId, (catcherPitchFloats.get(catcherId) ?? 0) + pitchesThisInning);
    }

    for (const [catcherId, pitches] of catcherPitchFloats.entries()) {
      const stats = result.get(catcherId);
      if (stats) stats.pitchesCaught = Math.round(pitches);
    }
  }

  return result;
}
